/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import React, { useState } from 'react';
import {
    IMoeV2FileDownloadViewProps,
    getFileIcon,
    InfoIcon,
    DownloadIcon,
    formatBytesToSize,
    detectFileTypeFromExtension
} from './moe-v2-file-download';

export default (props: IMoeV2FileDownloadViewProps) => {
    const { config } = props;
    const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

    const toggleDescription = (itemIndex: number) => {
        const itemId = `file-${itemIndex}`;
        setExpandedItems(prev => {
            const newSet = new Set(prev);
            if (newSet.has(itemId)) {
                newSet.delete(itemId);
            } else {
                newSet.add(itemId);
            }
            return newSet;
        });
    };

    const handleDownload = (fileUrl: string, fileName: string) => {
        // Create a temporary anchor element to trigger download
        const link = document.createElement('a');
        link.href = `${fileUrl}?download=1`;
        link.download = fileName;
        link.style.display = 'none';
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div className='moe-v2-module-container-file-download moe-file-download'>
            <h2 className='moe-file-download__title'>{config.title}</h2>
            <div className='moe-file-download__container'>
                <div className='moe-file-download__list'>
                    {config.files &&
                        config.files.map((fileItem, index) => {
                            const itemId = `file-${index}`;
                            const fileType = detectFileTypeFromExtension(fileItem.file?.extension || fileItem.file?.fileName || '');
                            const fileSize = formatBytesToSize(fileItem.file?.sizeInBytes || 0);
                            const fileName = fileItem.file?.fileName || fileItem.file?.title || `File ${index + 1}`;
                            const description = fileItem.description || fileItem.file?.description;

                            return (
                                <div key={itemId} className='moe-file-download__item'>
                                    <div className='moe-file-download__item-main'>
                                        <div className='moe-file-download__icon'>{getFileIcon(fileType)}</div>

                                        <div className='moe-file-download__content'>
                                            <span className='moe-file-download__filename'>{fileName}</span>
                                            {expandedItems.has(itemId) && description && (
                                                <p className='moe-file-download__description'>{description}</p>
                                            )}
                                        </div>

                                        <div className='moe-file-download__actions'>
                                            {/* Info icon only appears if file has a description */}
                                            {description && (
                                                <button
                                                    className='moe-file-download__info-btn'
                                                    onClick={() => toggleDescription(index)}
                                                    aria-label={`Toggle description for ${fileName}`}
                                                >
                                                    {InfoIcon}
                                                </button>
                                            )}

                                            <button
                                                className='moe-file-download__download-btn'
                                                onClick={() => handleDownload(fileItem.file?.url ?? '', fileName)}
                                                aria-label={`Download ${fileName}`}
                                            >
                                                <span className='moe-file-download__download-text'>Download</span>
                                                {DownloadIcon}
                                            </button>

                                            <span className='moe-file-download__file-info'>
                                                {fileType.toUpperCase()} | {fileSize}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    {(!config.files || config.files.length === 0) && (
                        <div className='moe-file-download__empty'>
                            <p>No files available for download.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
